import { WeightUnit } from "@redotech/cockroachdb-codegen/tables/product/weight-unit";
import { Service } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import {
  CanadaPostServices,
  Carriers,
  UpsServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { isUpsCarrier } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  FulfillmentGroup,
  FulfillmentGroupData,
  getFulfillmentGroupItems,
  getFulfillmentGroupValue,
  GroupSelectedRate,
  InsuranceSelectionCriteria,
  InsuranceSelectionType,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { FulfillmentOrderLineItem } from "@redotech/redo-fulfillment-group-common/fulfillment-order-line-item";
import {
  convertWeight,
  Weight,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import {
  type OutboundParcel,
  type ParcelInsuranceCoverageType,
} from "@redotech/redo-fulfillment-parcel-common/parcel";
import { assertNever } from "@redotech/util/type";
import { applyParcelWeightDistribution } from "./parcel-weight-distribution";

export const CARRIERS_WITHOUT_INSURANCE_SUPPORT: Carriers[] = [
  Carriers.ETA_EXPRESS,
  Carriers.LANDMARK_GLOBAL,
] as const;

export function carrierSupportsInsurance(carrier: Carriers): boolean {
  return !CARRIERS_WITHOUT_INSURANCE_SUPPORT.includes(carrier);
}

export const DEFAULT_INSURANCE_INCLUDED_THRESHOLD = 100;
export const DEFAULT_INSURANCE_RATE_PERCENTAGE = 1;

const ONE_LB_IN_GRAMS = 453.592;

export const DEFAULT_INSURANCE_PACKAGE_WEIGHT: Weight = {
  unit: WeightUnit.Lb,
  value: 2,
};

export enum NO_INSURANCE_REASON {
  BULK_EDIT_NOT_ALLOWED = "BULK_EDIT_NOT_ALLOWED",
  CARRIER_UNSUPPORTED = "CARRIER_UNSUPPORTED",
  PACKAGE_PROTECTED = "PACKAGE_PROTECTED",
  NO_CARRIER_SELECTED = "NO_CARRIER_SELECTED",
  NO_PARCELS_SELECTED = "NO_PARCELS_SELECTED",
}

export const NO_INSURANCE_REASON_MESSAGES: Record<NO_INSURANCE_REASON, string> =
  {
    [NO_INSURANCE_REASON.BULK_EDIT_NOT_ALLOWED]:
      "Cannot bulk edit insurance for selected shipments. Insurance inclusion can differ between some carriers, service levels, and package weights",
    [NO_INSURANCE_REASON.CARRIER_UNSUPPORTED]:
      "Carrier does not support insurance",
    [NO_INSURANCE_REASON.PACKAGE_PROTECTED]: "Package is already protected",
    [NO_INSURANCE_REASON.NO_CARRIER_SELECTED]:
      "No carrier selected for this shipment",
    // This one should never get shown in the UI
    [NO_INSURANCE_REASON.NO_PARCELS_SELECTED]:
      "No parcels selected for this shipment",
  };

export enum PARCEL_THRESHOLD_REASON {
  DEFAULT_CARRIER_THRESHOLD = "DEFAULT_CARRIER_THRESHOLD",
  DHL_ECOMMERCE_UNDER_ONE_LB = "DHL_ECOMMERCE_UNDER_ONE_LB",
  UPS_GROUND_SAVER = "UPS_GROUND_SAVER",
  CANADA_POST_REGULAR_PARCEL = "CANADA_POST_REGULAR_PARCEL",
  CARRIER_UNSUPPORTED = "CARRIER_UNSUPPORTED",
}

export const PARCEL_THRESHOLD_NOTE_MESSAGES: Record<
  PARCEL_THRESHOLD_REASON,
  string
> = {
  DEFAULT_CARRIER_THRESHOLD: "", // No UI label
  CARRIER_UNSUPPORTED: "Carrier does not support insurance",
  DHL_ECOMMERCE_UNDER_ONE_LB:
    "DHL eCommerce: no insurance coverage for packages under 1 lb",
  UPS_GROUND_SAVER: "UPS Ground Saver: no coverage after USPS handoff",
  CANADA_POST_REGULAR_PARCEL:
    "Canada Post Regular Parcel: no insurance included by default",
};

/**
 * Returns the carrier/service-specific insurance coverage threshold and any
 * additional note that should be displayed in the UI.
 *
 * - UPS Ground Saver (SurePost): $50 while carried by UPS, no coverage after
 *   USPS handoff.
 * - DHL (packages under 1 lb): no insurance coverage.
 * - All other carriers/services: team-configured threshold or $100 default.
 */
export function getInsuranceAmountIncludedByDefaultThreshold({
  carrier,
  service,
  teamInsuranceIncludedThreshold,
  packageWeight,
}: {
  carrier: Carriers | null;
  service: Service | null;
  teamInsuranceIncludedThreshold: number;
  packageWeight: Weight | null | undefined;
}): { parcelInsuranceThreshold: number; reason: PARCEL_THRESHOLD_REASON } {
  // If we've set a custom threshold for the team and it's not the default, use it
  // For context, shopify covers $200 by default, so to win some deals we need to cover $200 by default
  if (
    teamInsuranceIncludedThreshold &&
    teamInsuranceIncludedThreshold !== DEFAULT_INSURANCE_INCLUDED_THRESHOLD
  ) {
    return {
      parcelInsuranceThreshold: teamInsuranceIncludedThreshold,
      reason: PARCEL_THRESHOLD_REASON.DEFAULT_CARRIER_THRESHOLD,
    };
  }

  if (!carrier || !service) {
    return {
      parcelInsuranceThreshold: DEFAULT_INSURANCE_INCLUDED_THRESHOLD,
      reason: PARCEL_THRESHOLD_REASON.DEFAULT_CARRIER_THRESHOLD,
    };
  }

  const { threshold, reason } = carrierServiceInsuranceThreshold({
    carrier,
    service,
    packageWeight: packageWeight ?? DEFAULT_INSURANCE_PACKAGE_WEIGHT,
  });
  return { parcelInsuranceThreshold: threshold, reason };
}

export function fulfillmentGroupInsuranceEligible({
  fulfillmentGroup,
}: {
  fulfillmentGroup: FulfillmentGroupData;
}):
  | { insuranceEligible: true }
  | { insuranceEligible: false; reason: NO_INSURANCE_REASON } {
  if (fulfillmentGroup.protectedByPackageProtectionPlus) {
    return {
      insuranceEligible: false,
      reason: NO_INSURANCE_REASON.PACKAGE_PROTECTED,
    };
  }

  const selectedCarrier =
    fulfillmentGroup.selectedShippingRate?.carrier ?? null;
  if (!selectedCarrier) {
    return {
      insuranceEligible: false,
      reason: NO_INSURANCE_REASON.NO_CARRIER_SELECTED,
    };
  }
  if (!carrierSupportsInsurance(selectedCarrier)) {
    return {
      insuranceEligible: false,
      reason: NO_INSURANCE_REASON.CARRIER_UNSUPPORTED,
    };
  }

  return { insuranceEligible: true };
}

// The threshold at which a carrier/service covers packages by default
export function carrierServiceInsuranceThreshold({
  carrier,
  service,
  packageWeight,
}: {
  carrier: Carriers;
  service: Service;
  packageWeight: Weight;
}): { threshold: number; reason: PARCEL_THRESHOLD_REASON } {
  if (!carrierSupportsInsurance(carrier)) {
    return {
      threshold: 0,
      reason: PARCEL_THRESHOLD_REASON.CARRIER_UNSUPPORTED,
    };
  }

  const packageWeightGrams = convertWeight(packageWeight, WeightUnit.G).value;

  if (isDhlEcsUnderOneLb(carrier, packageWeightGrams)) {
    return {
      threshold: 0,
      reason: PARCEL_THRESHOLD_REASON.DHL_ECOMMERCE_UNDER_ONE_LB,
    };
  }

  if (isUpsGroundSaver(carrier, service)) {
    return { threshold: 50, reason: PARCEL_THRESHOLD_REASON.UPS_GROUND_SAVER };
  }

  if (isCanadaPostRegularParcel(carrier, service)) {
    return {
      threshold: 0,
      reason: PARCEL_THRESHOLD_REASON.CANADA_POST_REGULAR_PARCEL,
    };
  }

  return {
    threshold: DEFAULT_INSURANCE_INCLUDED_THRESHOLD,
    reason: PARCEL_THRESHOLD_REASON.DEFAULT_CARRIER_THRESHOLD,
  };
}

function isDhlEcsUnderOneLb(
  carrier: Carriers,
  packageWeightGrams: number | null,
): boolean {
  if (packageWeightGrams == null) {
    return false;
  }
  return carrier === Carriers.DHL_ECS && packageWeightGrams < ONE_LB_IN_GRAMS;
}

function isUpsGroundSaver(carrier: Carriers, service: Service): boolean {
  return (
    isUpsCarrier(carrier) &&
    (service === UpsServices.SurePostUnder1Lb ||
      service === UpsServices.SurePostOver1Lb)
  );
}

function isCanadaPostRegularParcel(
  carrier: Carriers,
  service: Service,
): boolean {
  return (
    carrier === Carriers.CANADA_POST &&
    service === CanadaPostServices.RegularParcel
  );
}

export const MIN_INSURANCE_COST = 0.7;

export function getParcelInsuranceCost({
  valueToInsure,
  teamRatePercentage,
  parcelInsuranceThreshold,
}: {
  valueToInsure: number;
  teamRatePercentage: number;
  parcelInsuranceThreshold: number;
}): number {
  // If the value of the package is less than the threshold at which insurance is included for free, don't charge anything
  if (valueToInsure <= parcelInsuranceThreshold) {
    return 0;
  }
  const rate = teamRatePercentage * 0.01;
  const costOfOnePackage = Math.max(rate * valueToInsure, MIN_INSURANCE_COST);
  return Math.round(costOfOnePackage * 100) / 100;
}

function roundMoneyAmount(amount: number): number {
  return Math.round(amount * 100) / 100;
}

function getFulfillmentGroupShippingCoverageAmount(
  group: FulfillmentGroup | FulfillmentGroupData,
): number {
  let numericRate: number | null | undefined;
  if ("summary" in group) {
    numericRate = group.summary.fulfillmentInfo?.numericRate;
  } else {
    numericRate = group.fulfillmentInfo?.numericRate;
  }

  const amount = Number(numericRate ?? 0);
  return Number.isFinite(amount) ? amount : 0;
}

function getInsuranceParcelsForGroup(
  group: FulfillmentGroup | FulfillmentGroupData,
): OutboundParcel[] {
  return applyParcelWeightDistribution(
    group.selectedParcels,
    getFulfillmentGroupItems(group),
  ).parcels;
}

function getParcelForInsuranceCap({
  group,
  parcel,
}: {
  group: FulfillmentGroup | FulfillmentGroupData;
  parcel: OutboundParcel;
}): OutboundParcel {
  if (parcel.itemAllocations?.length) {
    return parcel;
  }

  const selectedParcels = group.selectedParcels ?? [];
  const normalizedParcels = getInsuranceParcelsForGroup(group);
  const parcelIndex = selectedParcels.indexOf(parcel);
  return (
    normalizedParcels[parcelIndex] ??
    (normalizedParcels.length === 1 ? normalizedParcels[0] : undefined) ??
    parcel
  );
}

export function getPackageItemListingPrice({
  parcel,
  lineItems,
}: {
  parcel: OutboundParcel | null | undefined;
  lineItems: readonly FulfillmentOrderLineItem[];
}): number {
  if (!parcel) {
    return 0;
  }

  const lineItemsByExternalId = new Map(
    lineItems.map((lineItem) => [lineItem.externalId, lineItem]),
  );
  const listingPrice = (parcel.itemAllocations ?? []).reduce(
    (sum, allocation) => {
      const lineItem = lineItemsByExternalId.get(allocation.externalId);
      if (!lineItem || allocation.quantity <= 0) {
        return sum;
      }
      return sum + Number(lineItem.unitPrice.amount) * allocation.quantity;
    },
    0,
  );

  return roundMoneyAmount(listingPrice);
}

export function getShippingClaimGoodsValue(
  group: FulfillmentGroup | FulfillmentGroupData,
): number {
  const exchangeMarkdown = getFulfillmentGroupItems(group).reduce(
    (total, lineItem) => {
      if (!lineItem.isExchangeItem) {
        return total;
      }

      const listingPrice = Number(lineItem.unitPrice.amount);
      const exchangePrice = Number(lineItem.unitDiscountedPrice.amount);
      return total + (listingPrice - exchangePrice) * lineItem.quantity;
    },
    0,
  );

  return roundMoneyAmount(getFulfillmentGroupValue(group) + exchangeMarkdown);
}

function getPackageShippingCoverageAmount({
  packageItemListingPrice,
  totalItemListingPrice,
  shippingCost,
  packageCount,
}: {
  packageItemListingPrice: number;
  totalItemListingPrice: number;
  shippingCost: number;
  packageCount: number;
}): number {
  if (shippingCost <= 0 || packageCount <= 0) {
    return 0;
  }
  if (totalItemListingPrice <= 0) {
    return shippingCost / packageCount;
  }
  return (shippingCost * packageItemListingPrice) / totalItemListingPrice;
}

function getPackageInsuranceCoverageCap({
  parcel,
  parcels,
  lineItems,
  shippingCost = 0,
}: {
  parcel: OutboundParcel | null | undefined;
  parcels: readonly OutboundParcel[];
  lineItems: readonly FulfillmentOrderLineItem[];
  shippingCost?: number;
}): number {
  if (!parcel) {
    return 0;
  }

  const packageItemListingPrice = getPackageItemListingPrice({
    parcel,
    lineItems,
  });
  const totalShippingCost = Number.isFinite(shippingCost) ? shippingCost : 0;
  const totalItemListingPrice = parcels.reduce((sum, packageParcel) => {
    return (
      sum + getPackageItemListingPrice({ parcel: packageParcel, lineItems })
    );
  }, 0);
  const packageShippingCoverageAmount = getPackageShippingCoverageAmount({
    packageItemListingPrice,
    totalItemListingPrice,
    shippingCost: totalShippingCost,
    packageCount: parcels.length,
  });
  return roundMoneyAmount(
    packageItemListingPrice + packageShippingCoverageAmount,
  );
}

function getFulfillmentGroupInsuranceCoverageCap({
  group,
  selectedParcels = group.selectedParcels ?? [],
  shippingCost,
}: {
  group: FulfillmentGroup | FulfillmentGroupData;
  selectedParcels?: OutboundParcel[];
  shippingCost?: number;
}): number {
  const lineItems = getFulfillmentGroupItems(group);
  const parcels = applyParcelWeightDistribution(
    selectedParcels,
    lineItems,
  ).parcels;
  const shippingCoverageAmount =
    shippingCost ?? getFulfillmentGroupShippingCoverageAmount(group);

  return roundMoneyAmount(
    parcels.reduce((sum, parcel) => {
      return (
        sum +
        getPackageInsuranceCoverageCap({
          parcel,
          parcels,
          lineItems,
          shippingCost: shippingCoverageAmount,
        })
      );
    }, 0),
  );
}

export function getFulfillmentGroupParcelMaxInsuredValues({
  group,
  selectedParcels = group.selectedParcels ?? [],
  shippingCost,
}: {
  group: FulfillmentGroup | FulfillmentGroupData;
  selectedParcels?: OutboundParcel[];
  shippingCost?: number;
}): { parcel: OutboundParcel; maxInsuredValue: number }[] {
  const maxInsuredValue = getFulfillmentGroupInsuranceCoverageCap({
    group,
    selectedParcels,
    shippingCost,
  });

  return selectedParcels.map((parcel) => ({ parcel, maxInsuredValue }));
}

export function calculateInsuranceSelectionAmount({
  insuranceSelectionCriteria,
  maxInsuredValue,
}: {
  insuranceSelectionCriteria: InsuranceSelectionCriteria;
  maxInsuredValue: number;
}): number | null {
  switch (insuranceSelectionCriteria.type) {
    case InsuranceSelectionType.PERCENTAGE: {
      const percentage = Math.min(
        Math.max(insuranceSelectionCriteria.percentage, 0),
        100,
      );
      if (percentage === 0) {
        return null;
      }
      return roundMoneyAmount(maxInsuredValue * (percentage / 100));
    }
    case InsuranceSelectionType.FIXED:
      return insuranceSelectionCriteria.amount > 0
        ? insuranceSelectionCriteria.amount
        : null;
    default:
      assertNever(insuranceSelectionCriteria);
  }
}

export function parcelInsuranceSelectionsEqual(
  previousParcels: readonly OutboundParcel[] | null | undefined,
  nextParcels: readonly OutboundParcel[] | null | undefined,
): boolean {
  const parcelCount = Math.max(
    previousParcels?.length ?? 0,
    nextParcels?.length ?? 0,
  );
  for (let index = 0; index < parcelCount; index++) {
    const previousInsurance = previousParcels?.[index]?.insurance;
    const nextInsurance = nextParcels?.[index]?.insurance;
    if (
      (previousInsurance?.includeInsurance ?? null) !==
        (nextInsurance?.includeInsurance ?? null) ||
      (previousInsurance?.insuredAmount ?? null) !==
        (nextInsurance?.insuredAmount ?? null) ||
      (previousInsurance?.coverageType ?? null) !==
        (nextInsurance?.coverageType ?? null)
    ) {
      return false;
    }
  }
  return true;
}

export function removeAdditionalInsuranceFromParcels(
  parcels: readonly OutboundParcel[],
): OutboundParcel[] {
  return parcels.map((parcel) => {
    const insurance = parcel.insurance;
    const hasAdditionalInsurance =
      insurance?.insuredAmount != null ||
      insurance?.includeInsurance === true ||
      insurance?.coverageType === "custom" ||
      insurance?.coverageType === "full";
    if (!hasAdditionalInsurance) {
      return parcel;
    }

    return {
      ...parcel,
      insurance: { insuredAmount: null, coverageType: "included" },
    };
  });
}

export function reconcileParcelInsuranceForSelectionCriteria({
  group,
  insuranceSelectionCriteria,
  teamInsuranceIncludedThreshold,
  selectedShippingRate = group.selectedShippingRate,
  shippingCost,
}: {
  group: FulfillmentGroup | FulfillmentGroupData;
  insuranceSelectionCriteria: InsuranceSelectionCriteria;
  teamInsuranceIncludedThreshold: number;
  selectedShippingRate?: Pick<GroupSelectedRate, "carrier" | "service"> | null;
  shippingCost?: number;
}): OutboundParcel[] | null {
  const selectedParcels = group.selectedParcels ?? [];
  if (selectedParcels.length === 0) {
    return null;
  }

  const carrier = selectedShippingRate?.carrier ?? null;
  const service = selectedShippingRate?.service ?? null;
  const parcelMaxInsuredValues = getFulfillmentGroupParcelMaxInsuredValues({
    group,
    selectedParcels,
    shippingCost,
  });
  const fullCoverageSelected =
    insuranceSelectionCriteria.type === InsuranceSelectionType.PERCENTAGE &&
    insuranceSelectionCriteria.percentage === 100;
  let changed = false;

  const reconciledParcels = parcelMaxInsuredValues.map(
    ({ parcel, maxInsuredValue }) => {
      const { parcelInsuranceThreshold } =
        getInsuranceAmountIncludedByDefaultThreshold({
          carrier,
          service,
          teamInsuranceIncludedThreshold,
          packageWeight: parcel.parcelAndContentWeight,
        });
      const insuranceSupported = !carrier || carrierSupportsInsurance(carrier);
      const insuredAmount =
        insuranceSupported &&
        (fullCoverageSelected || maxInsuredValue > parcelInsuranceThreshold)
          ? calculateInsuranceSelectionAmount({
              insuranceSelectionCriteria,
              maxInsuredValue,
            })
          : null;
      let coverageType: ParcelInsuranceCoverageType = "included";
      if (insuredAmount != null) {
        coverageType = fullCoverageSelected ? "full" : "custom";
      }
      const currentInsurance = parcel.insurance;
      if (
        (currentInsurance?.insuredAmount ?? null) === insuredAmount &&
        currentInsurance?.includeInsurance == null &&
        currentInsurance?.coverageType === coverageType
      ) {
        return parcel;
      }

      changed = true;
      return { ...parcel, insurance: { insuredAmount, coverageType } };
    },
  );

  return changed ? reconciledParcels : null;
}

export function reconcileParcelInsuranceForShippingCost({
  group,
  shippingCost,
}: {
  group: FulfillmentGroup | FulfillmentGroupData;
  shippingCost: number;
}): OutboundParcel[] | null {
  const selectedParcels = group.selectedParcels ?? [];
  const previousMaxInsuredValues = getFulfillmentGroupParcelMaxInsuredValues({
    group,
    selectedParcels,
  });
  const nextMaxInsuredValues = getFulfillmentGroupParcelMaxInsuredValues({
    group,
    selectedParcels,
    shippingCost,
  });
  let changed = false;

  const reconciledParcels = selectedParcels.map((parcel, index) => {
    const insuredAmount = parcel.insurance?.insuredAmount;
    const coverageType = parcel.insurance?.coverageType;
    const previousMaxInsuredValue = previousMaxInsuredValues[index];
    const nextMaxInsuredValue = nextMaxInsuredValues[index];
    if (
      coverageType === "custom" ||
      coverageType === "included" ||
      !previousMaxInsuredValue ||
      !nextMaxInsuredValue
    ) {
      return parcel;
    }

    let nextInsuredAmount = insuredAmount ?? null;
    if (
      coverageType === "full" ||
      insuredAmount === previousMaxInsuredValue.maxInsuredValue
    ) {
      nextInsuredAmount = nextMaxInsuredValue.maxInsuredValue;
    } else if (insuredAmount != null) {
      nextInsuredAmount = Math.min(
        insuredAmount,
        nextMaxInsuredValue.maxInsuredValue,
      );
    }
    if (nextInsuredAmount === insuredAmount) {
      return parcel;
    }

    changed = true;
    return {
      ...parcel,
      insurance: { ...parcel.insurance, insuredAmount: nextInsuredAmount },
    };
  });

  return changed ? reconciledParcels : null;
}

export function getPackageInsuranceCoverageCapForGroup({
  group,
  parcel,
}: {
  group: FulfillmentGroup | FulfillmentGroupData;
  parcel: OutboundParcel | null | undefined;
}): number {
  if (!parcel) {
    return 0;
  }

  const lineItems = getFulfillmentGroupItems(group);
  const parcels = applyParcelWeightDistribution(
    group.selectedParcels,
    lineItems,
  ).parcels;
  return getPackageInsuranceCoverageCap({
    parcel: getParcelForInsuranceCap({ group, parcel }),
    parcels,
    lineItems,
    shippingCost: getFulfillmentGroupShippingCoverageAmount(group),
  });
}

export function getFulfillmentGroupPackageInsuranceCoverageCap(
  group: FulfillmentGroup | FulfillmentGroupData,
): number {
  return getFulfillmentGroupInsuranceCoverageCap({ group });
}

export function getTotalInsuranceCostForGroups({
  groups,
  teamRatePercentage,
  teamInsuranceIncludedThreshold,
}: {
  groups: readonly FulfillmentGroupData[];
  teamRatePercentage: number;
  teamInsuranceIncludedThreshold: number;
}): number {
  return groups.reduce((sum, group) => {
    return (
      sum +
      getTotalInsuranceCostForGroup({
        group,
        teamRatePercentage,
        teamInsuranceIncludedThreshold,
      })
    );
  }, 0);
}

export function getTotalInsuranceCostForGroup({
  group,
  teamRatePercentage,
  teamInsuranceIncludedThreshold,
}: {
  group: FulfillmentGroup | FulfillmentGroupData;
  teamRatePercentage: number;
  teamInsuranceIncludedThreshold: number;
}): number {
  const costForGroup = getInsuranceParcelsForGroup(group).reduce(
    (sum, parcel) => {
      const { parcelInsuranceThreshold } =
        getInsuranceAmountIncludedByDefaultThreshold({
          carrier: group.selectedShippingRate?.carrier ?? null,
          service: group.selectedShippingRate?.service ?? null,
          teamInsuranceIncludedThreshold,
          packageWeight: parcel.parcelAndContentWeight,
        });
      const parcelValue = getInsuranceValueSingleParcel({
        group,
        parcel,
        parcelInsuranceThreshold,
      });
      const cost = getParcelInsuranceCost({
        teamRatePercentage,
        valueToInsure: parcelValue,
        parcelInsuranceThreshold,
      });
      return sum + cost;
    },
    0,
  );

  return costForGroup;
}

export function getFulfillmentGroupInsuredValue({
  group,
  teamInsuranceIncludedThreshold,
}: {
  group: FulfillmentGroup | FulfillmentGroupData;
  teamInsuranceIncludedThreshold: number;
}): number {
  const insuredValueForGroup = getInsuranceParcelsForGroup(group).reduce(
    (sum, parcel) => {
      const { parcelInsuranceThreshold } =
        getInsuranceAmountIncludedByDefaultThreshold({
          carrier: group.selectedShippingRate?.carrier ?? null,
          service: group.selectedShippingRate?.service ?? null,
          teamInsuranceIncludedThreshold,
          packageWeight: parcel.parcelAndContentWeight,
        });
      const parcelValue = getInsuranceValueSingleParcel({
        group,
        parcel,
        parcelInsuranceThreshold,
      });
      return sum + parcelValue;
    },
    0,
  );

  return insuredValueForGroup;
}

export function getExplicitInsuredParcelsValue(
  fulfillmentGroup: FulfillmentGroup | FulfillmentGroupData,
): number {
  return (
    fulfillmentGroup.selectedParcels?.reduce(
      (sum, parcel) => sum + (parcel.insurance?.insuredAmount ?? 0),
      0,
    ) ?? 0
  );
}

// The default for every parcel is the min between its package coverage cap and
// the default threshold. Explicit parcel coverage can go up to the full group
// cap because item-to-parcel assignment is not authoritative in OMS.
export function getInsuranceValueSingleParcel({
  group,
  parcel,
  parcelInsuranceThreshold,
}: {
  group: FulfillmentGroup | FulfillmentGroupData;
  parcel: OutboundParcel;
  parcelInsuranceThreshold: number;
}): number {
  const packageInsuranceCoverageCap = getPackageInsuranceCoverageCapForGroup({
    group,
    parcel,
  });

  const defaultParcelCoverage = Math.min(
    packageInsuranceCoverageCap,
    parcelInsuranceThreshold,
  );
  switch (parcel.insurance?.coverageType) {
    case "included":
      return defaultParcelCoverage;
    case "full":
      return getFulfillmentGroupInsuranceCoverageCap({ group });
    case "custom":
    case null:
    case undefined:
      break;
  }

  // Backwards compatibility: old records with includeInsurance=true and no
  // coverageType are treated as 100% of order value (same as coverageType "full").
  if (
    parcel.insurance?.coverageType == null &&
    parcel.insurance?.includeInsurance
  ) {
    return getFulfillmentGroupInsuranceCoverageCap({ group });
  }

  const explicitInsuredAmount = parcel.insurance?.insuredAmount;
  if (explicitInsuredAmount == null) {
    return defaultParcelCoverage;
  }

  return Math.min(
    explicitInsuredAmount,
    getFulfillmentGroupInsuranceCoverageCap({ group }),
  );
}

export function getTotalValueToInsure(
  fulfillmentGroup: FulfillmentGroup | FulfillmentGroupData,
  insuranceIncludedThreshold: number,
) {
  const insuranceParcels = getInsuranceParcelsForGroup(fulfillmentGroup);
  if (insuranceParcels.length > 1) {
    return insuranceParcels.reduce((acc, parcel) => {
      const parcelInsuranceValue = getInsuranceValueSingleParcel({
        group: fulfillmentGroup,
        parcel,
        parcelInsuranceThreshold: insuranceIncludedThreshold,
      });
      return acc + parcelInsuranceValue;
    }, 0);
  }

  const firstParcel = insuranceParcels[0];
  if (!firstParcel) {
    return 0;
  }

  return getInsuranceValueSingleParcel({
    group: fulfillmentGroup,
    parcel: firstParcel,
    parcelInsuranceThreshold: insuranceIncludedThreshold,
  });
}

export function getInsuredValue(
  fulfillmentGroup: FulfillmentGroup | FulfillmentGroupData,
  trackingNumber: string,
  insuranceIncludedThreshold: number,
) {
  if (fulfillmentGroup.additionalShipments?.length) {
    const shipments = [
      fulfillmentGroup.shipment,
      ...(fulfillmentGroup.additionalShipments ?? []),
    ];
    const shipmentIndex = shipments.findIndex(
      (shipment) => shipment?.tracker?.trackingCode === trackingNumber,
    );
    const parcel = shipments[shipmentIndex]?.parcel;

    if (!parcel) {
      return 0;
    }

    const normalizedParcel =
      getInsuranceParcelsForGroup(fulfillmentGroup)[shipmentIndex];
    const value = getInsuranceValueSingleParcel({
      group: fulfillmentGroup,
      parcel: normalizedParcel
        ? {
            ...normalizedParcel,
            insurance: parcel.insurance ?? normalizedParcel.insurance,
          }
        : parcel,
      parcelInsuranceThreshold: insuranceIncludedThreshold,
    });

    return value;
  }

  const firstParcel = getInsuranceParcelsForGroup(fulfillmentGroup)[0];
  if (!firstParcel) {
    return 0;
  }

  return getInsuranceValueSingleParcel({
    group: fulfillmentGroup,
    parcel: firstParcel,
    parcelInsuranceThreshold: insuranceIncludedThreshold,
  });
}
